import {
  ActionIcon,
  Anchor,
  Alert,
  Button,
  Code,
  Combobox,
  Divider,
  Loader,
  ScrollArea,
  SegmentedControl,
  Text,
  TextInput,
  Tooltip,
  useCombobox,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import type { TestType } from '@prisma/client';
import {
  IconChevronDown,
  IconChevronUp,
  IconCornerDownLeft,
  IconDeviceMobile,
  IconLoader,
  IconWorld,
  IconX,
  IconCopy,
  IconSearch,
  IconComponents,
  IconLink,
} from '@tabler/icons-react';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import router from 'next/router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useProjectId } from '~/hooks/context/useProjectId';
import { getTestDraftPath } from '~/pages/project/[projectId]/testDraft/[testDraftId]';
import { api } from '~/trpc-extra/api';
import { isLocalhostLike, LOCALHOST_TUNNEL_DOC, normalizeWebsiteUrl } from '~/utils/url';

// Extract navigation URL from component operations
const getComponentNavigationUrl = (
  component: { operations?: { type: string; navigationOp?: { value: string } | null }[] | null } | null,
): string => component?.operations?.find((op) => op.type === 'NAVIGATION')?.navigationOp?.value || '';

const zQuickRecordForm = z.object({
  websiteUnderTest: z
    .string()
    .trim()
    .transform((value) => (value ? normalizeWebsiteUrl(value) : value))
    .refine(
      (value) => {
        // Allow empty value - will be handled by backend when component/test is selected
        if (!value) {
          return true;
        }
        try {
          const url = new URL(value);
          return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
          return false;
        }
      },
      { message: 'Please enter a valid website URL (e.g., https://example.com)' },
    ),
  testType: z.enum(['WEB' satisfies TestType, 'WEB_MOBILE' satisfies TestType]),
  testName: z.string().optional(),
});

export default function QuickRecordStart({ testName, userFlowId }: { testName?: string; userFlowId?: string }) {
  const projectId = useProjectId();
  const trpc = api.useUtils();

  const combobox = useCombobox();
  const [selectedTestToCopy, setSelectedTestToCopy] = useState<string | null>(null);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Note: since this takes some time to load, there can be a brief moment where it loads in and sets the form
  // This looks weird. The fix is probably to pre-fetch before we open this modal that shows this component.
  const { data: projectWebsites = [], isLoading: projectWebsitesLoading } = api.project.getProjectWebsites.useQuery(
    { projectId: projectId || '' },
    { enabled: !!projectId },
  );

  // Fetch components for the project
  const { data: components = [], isLoading: componentsLoading } = api.component.list.useQuery(
    { projectId: projectId || '' },
    { enabled: !!projectId },
  );

  // Fetch recent tests for quick start
  const { data: recentTestsData, isLoading: testsLoading } = api.test.listTestsForQuickStart.useQuery(
    { projectId: projectId || '' },
    { enabled: !!projectId },
  );
  const recentTests = recentTestsData ?? [];

  const { mutate: addProjectWebsite } = api.project.addProjectWebsite.useMutation();
  const { mutate: createTestDraft, isPending: isCreating } = api.testDraft.createTestDraft.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Creating your test...', color: 'blue' });
    },
    onSuccess: async (testDraft, { websiteUnderTest }) => {
      await router.push(getTestDraftPath({ projectId: projectId || '', testDraftId: testDraft.id }));
      trpc.testDraft.list.invalidate({ projectId: projectId || '' }).catch(() => undefined);
      modals.closeAll();
      if (websiteUnderTest) {
        addProjectWebsite({ projectId: projectId || '', url: websiteUnderTest });
      }
    },
  });

  const form = useForm({
    initialValues: {
      websiteUnderTest: projectWebsites.at(0)?.url || '',
      testType: 'WEB' satisfies TestType,
      testName,
    },
    validate: zod4Resolver(zQuickRecordForm),
    transformValues: (values) => zQuickRecordForm.parse(values),
  });

  useEffect(() => {
    if (!projectWebsitesLoading && projectWebsites.length > 0) {
      form.setFieldValue('websiteUnderTest', projectWebsites.at(0)?.url || '');
    }
  }, [projectWebsites]);

  // Autocomplete filter - use searchQuery instead of form value
  const filteredWebsites = projectWebsites.filter((website) => {
    const query = searchQuery.toLowerCase();
    return query === '' ? true : website.url.toLowerCase().includes(query);
  });

  // Filter recent tests based on search query
  const filteredRecentTests = recentTests.filter((test): boolean => {
    const query = searchQuery.toLowerCase();
    if (query === '') {
      return true;
    }
    if (test.name.toLowerCase().includes(query)) {
      return true;
    }
    if ((test.firstStepUrl || '').toLowerCase().includes(query)) {
      return true;
    }
    if ((test.firstStepDescription || '').toLowerCase().includes(query)) {
      return true;
    }
    return Boolean(test.subsequentSteps.some((step): boolean => String(step).toLowerCase().includes(query)));
  });

  // Filter components based on search query (excluding archived components)
  const filteredComponents = components.filter((component) => {
    // Exclude archived components from the quick start dropdown
    if (component.archived) {
      return false;
    }
    const query = searchQuery.toLowerCase();
    const componentUrl = getComponentNavigationUrl(component);
    return query === '' || component.name.toLowerCase().includes(query) || componentUrl.toLowerCase().includes(query);
  });

  const handleCopyFromTest = (test: (typeof recentTests)[number]) => {
    setSelectedTestToCopy(test.id);
    setSelectedComponentId(null); // Clear component selection
    form.setFieldValue('websiteUnderTest', test.firstStepUrl || '');
    form.setFieldValue('testName', `Copy of ${test.name}`);
    setSearchQuery(''); // Clear search after selection
    combobox.closeDropdown();
  };

  const handleSelectComponent = (component: (typeof components)[number]) => {
    setSelectedTestToCopy(null); // Clear test selection
    setSelectedComponentId(component.id);
    form.setFieldValue('websiteUnderTest', getComponentNavigationUrl(component));
    form.setFieldValue('testName', `Test ${component.name}`);
    setSearchQuery(''); // Clear search after selection
    combobox.closeDropdown();
  };

  // Get display value for the input - returns { display, full } for tooltip support
  const getDisplayValue = (): { display: string; full: string } => {
    if (combobox.dropdownOpened) {
      return { display: searchQuery, full: searchQuery }; // Show search query when dropdown is open
    }

    if (selectedTestToCopy) {
      const test = recentTests.find((t) => t.id === selectedTestToCopy);
      if (test) {
        // Show the first step prominently, with test name as context
        const firstStep = test.firstStepDescription;
        const fullText = `${firstStep} (from "${test.name}")`;
        const truncatedName = test.name.length > 30 ? `${test.name.slice(0, 30)}...` : test.name;
        const displayText = `${firstStep} (from "${truncatedName}")`;
        // Truncate the entire thing if still too long
        const truncatedDisplay = displayText.length > 65 ? `${displayText.slice(0, 65)}...` : displayText;
        return { display: truncatedDisplay, full: fullText };
      }
    }

    if (selectedComponentId) {
      const component = components.find((c) => c.id === selectedComponentId);
      if (component) {
        const text = `Component: ${component.name}`;
        return { display: text, full: text };
      }
    }

    return { display: form.values.websiteUnderTest, full: form.values.websiteUnderTest }; // Show URL when manually entered
  };

  const displayValue = getDisplayValue();

  const handleSubmit = form.onSubmit(async ({ websiteUnderTest, testType }) => {
    // Only validate URL if neither component nor test is selected
    if (!selectedComponentId && !selectedTestToCopy) {
      if (!websiteUnderTest) {
        notifications.show({
          color: 'red',
          message: 'Please enter a URL or select a component/test',
        });
        return;
      }
      if (isLocalhostLike(websiteUnderTest)) {
        notifications.show({
          color: 'red',
          message: 'Cannot test localhost URLs. Use the Stably CLI: stably dev',
        });
        return;
      }
    }

    createTestDraft({
      projectId: projectId || '',
      websiteUnderTest: websiteUnderTest || undefined,
      testName: form.values.testName,
      runOnMobile: testType === 'WEB_MOBILE', // TODO: Temporary for backward compatibility
      testType: testType as TestType,
      userFlowId,
      componentId: selectedComponentId || undefined,
      copyFirstStepFromTestId: selectedTestToCopy || undefined,
    });
  });

  return (
    <div className="flex flex-col px-6 py-6">
      <div className="mb-6 flex items-center justify-between">
        <Text className="text-lg font-bold">Create a test</Text>
        <ActionIcon
          variant="subtle"
          onClick={() => modals.closeAll()}
          color="gray"
          radius="xl"
          size="sm"
        >
          <IconX size={16} />
        </ActionIcon>
      </div>
      <form
        className="flex flex-col"
        onSubmit={handleSubmit}
      >
        <div>
          <SegmentedControl
            {...form.getInputProps('testType')}
            className="w-full"
            data={[
              {
                label: (
                  <div className="flex items-center justify-center gap-2.5 py-1">
                    <IconWorld size={16} />
                    <span>Web</span>
                  </div>
                ),
                value: 'WEB' satisfies TestType,
              },
              {
                label: (
                  <div className="flex items-center justify-center gap-2.5 py-1">
                    <IconDeviceMobile size={16} />
                    <span>Mobile Web</span>
                  </div>
                ),
                value: 'WEB_MOBILE' satisfies TestType,
              },
            ]}
          />
        </div>

        <div className="mt-6">
          <Text className="mb-2 text-sm font-medium">First step</Text>
          <Combobox
            store={combobox}
            onOptionSubmit={(value) => {
              form.setFieldValue('websiteUnderTest', value);
              setSelectedTestToCopy(null); // Clear test selection when URL is picked
              setSelectedComponentId(null); // Clear component selection when URL is picked
              setSearchQuery(value); // Update search query to match selected value
              combobox.closeDropdown();
            }}
          >
            <Combobox.Target>
              <Tooltip
                label={displayValue.full}
                disabled={displayValue.display === displayValue.full || combobox.dropdownOpened}
                position="top"
                multiline
                maw={400}
              >
                <TextInput
                  className="w-full"
                  readOnly={projectWebsitesLoading}
                  leftSection={
                    projectWebsitesLoading ?
                      <IconLoader
                        size={16}
                        className="text-gray-500"
                      />
                    : selectedTestToCopy && !combobox.dropdownOpened ?
                      <IconCopy
                        size={16}
                        className="text-blue-500"
                      />
                    : selectedComponentId && !combobox.dropdownOpened ?
                      <IconComponents
                        size={16}
                        className="text-blue-500"
                      />
                    : !selectedTestToCopy && !selectedComponentId && form.values.websiteUnderTest ?
                      <IconLink
                        size={16}
                        className="text-blue-500"
                      />
                    : <IconSearch
                        size={16}
                        className="text-gray-400"
                      />

                  }
                  placeholder={projectWebsitesLoading ? 'Loading...' : 'Enter URL or search...'}
                  size="sm"
                  radius="md"
                  value={displayValue.display}
                  rightSection={
                    <ActionIcon
                      variant="transparent"
                      size="xs"
                      color="gray"
                      onClick={(event) => {
                        event.stopPropagation();
                        combobox.toggleDropdown();
                      }}
                    >
                      {combobox.dropdownOpened ?
                        <IconChevronUp size={14} />
                      : <IconChevronDown size={14} />}
                    </ActionIcon>
                  }
                  onClick={() => {
                    combobox.openDropdown();
                    if (selectedTestToCopy) {
                      setSearchQuery('');
                    }
                  }}
                  onFocus={() => {
                    combobox.openDropdown();
                    if (selectedTestToCopy) {
                      setSearchQuery('');
                    }
                  }}
                  onChange={(event) => {
                    const newValue = event.currentTarget.value;
                    setSearchQuery(newValue);
                    setSelectedTestToCopy(null); // Clear selection when typing
                    setSelectedComponentId(null); // Clear component selection when typing
                    form.setFieldValue('websiteUnderTest', newValue);
                    combobox.openDropdown();
                  }}
                  onKeyDown={(event) => {
                    // When user presses Enter and there are no matching dropdown options, accept the input as URL
                    if (
                      event.key === 'Enter' &&
                      searchQuery.trim() &&
                      filteredWebsites.length === 0 &&
                      filteredRecentTests.length === 0 &&
                      filteredComponents.length === 0
                    ) {
                      event.preventDefault();
                      form.setFieldValue('websiteUnderTest', searchQuery);
                      setSelectedTestToCopy(null);
                      setSelectedComponentId(null);
                      combobox.closeDropdown();
                    }
                  }}
                />
              </Tooltip>
            </Combobox.Target>
            <Combobox.Dropdown>
              <Combobox.Options>
                <ScrollArea.Autosize
                  className="max-h-[400px]"
                  scrollbarSize={4}
                >
                  {filteredWebsites.length > 0 && (
                    <>
                      <Divider
                        my="xs"
                        label={
                          <Text
                            size="xs"
                            className="font-medium text-gray-500 dark:text-gray-400"
                          >
                            Recent URLs
                          </Text>
                        }
                        labelPosition="center"
                      />
                      {filteredWebsites.slice(0, 2).map((website) => (
                        <Combobox.Option
                          value={website.url}
                          key={website.url}
                        >
                          {website.url}
                        </Combobox.Option>
                      ))}
                    </>
                  )}

                  {filteredComponents.length > 0 && (
                    <>
                      {filteredWebsites.length > 0 && (
                        <Divider
                          my="xs"
                          label={
                            <Text
                              size="xs"
                              className="font-medium text-gray-500 dark:text-gray-400"
                            >
                              Start with component:
                            </Text>
                          }
                          labelPosition="center"
                        />
                      )}
                      {filteredWebsites.length === 0 && (
                        <div className="px-2 py-1.5">
                          <Text
                            size="xs"
                            className="font-medium text-gray-500 dark:text-gray-400"
                          >
                            Start with component:
                          </Text>
                        </div>
                      )}
                      {filteredComponents.map((component) => {
                        const componentUrl = getComponentNavigationUrl(component);
                        return (
                          <div
                            key={component.id}
                            role="option"
                            tabIndex={0}
                            className="cursor-pointer px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:hover:bg-gray-700 dark:focus:bg-gray-700"
                            onClick={() => handleSelectComponent(component)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSelectComponent(component);
                              }
                            }}
                          >
                            <div className="flex items-start gap-2">
                              <IconComponents
                                size={16}
                                className="mt-0.5 shrink-0 text-blue-500"
                              />
                              <div className="flex-1">
                                <Text
                                  size="sm"
                                  className="font-medium"
                                >
                                  {component.name}
                                </Text>
                                {componentUrl ?
                                  <Text
                                    size="xs"
                                    className="mt-0.5 text-gray-400 dark:text-gray-500"
                                  >
                                    {componentUrl}
                                  </Text>
                                : null}
                                {!componentUrl && component.operations && component.operations.length > 0 ?
                                  <Text
                                    size="xs"
                                    className="text-gray-500 dark:text-gray-400"
                                  >
                                    {`${component.operations.length} operation${component.operations.length !== 1 ? 's' : ''}`}
                                  </Text>
                                : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {filteredRecentTests.length > 0 && (
                    <>
                      {(filteredWebsites.length > 0 || filteredComponents.length > 0) && (
                        <Divider
                          my="xs"
                          label={
                            <Text
                              size="xs"
                              className="font-medium text-gray-500 dark:text-gray-400"
                            >
                              Copy first step from:
                            </Text>
                          }
                          labelPosition="center"
                        />
                      )}
                      {filteredWebsites.length === 0 && filteredComponents.length === 0 && (
                        <div className="px-2 py-1.5">
                          <Text
                            size="xs"
                            className="font-medium text-gray-500 dark:text-gray-400"
                          >
                            Copy first step from:
                          </Text>
                        </div>
                      )}
                      {filteredRecentTests.map((test) => (
                        <div
                          key={test.id}
                          role="option"
                          tabIndex={0}
                          className="cursor-pointer px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:hover:bg-gray-700 dark:focus:bg-gray-700"
                          onClick={() => handleCopyFromTest(test)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleCopyFromTest(test);
                            }
                          }}
                        >
                          <div className="flex items-start gap-2">
                            <IconCopy
                              size={16}
                              className="mt-0.5 shrink-0 text-gray-400"
                            />
                            <div className="flex-1">
                              <Text
                                size="sm"
                                className="font-medium"
                              >
                                {test.name}
                              </Text>
                              <div className="mt-1.5 space-y-1">
                                <div className="rounded-md bg-blue-50 px-2 py-1.5 dark:bg-blue-950">
                                  <Text
                                    size="xs"
                                    className="font-medium text-blue-700 dark:text-blue-300"
                                  >
                                    First step: {test.firstStepDescription}
                                  </Text>
                                  {test.firstStepUrl ?
                                    <Text
                                      size="xs"
                                      className="mt-0.5 text-blue-600 dark:text-blue-400"
                                    >
                                      {test.firstStepUrl}
                                    </Text>
                                  : null}
                                </div>
                                {test.subsequentSteps.map((step, index) => (
                                  <Text
                                    key={index}
                                    size="xs"
                                    className="pl-2 text-gray-500 dark:text-gray-400"
                                  >
                                    Then: {step}
                                  </Text>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {componentsLoading ||
                    (testsLoading && (
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Loader size="xs" />
                        <Text
                          size="sm"
                          className="text-gray-500"
                        >
                          Loading options...
                        </Text>
                      </div>
                    ))}

                  {filteredWebsites.length === 0 &&
                    filteredRecentTests.length === 0 &&
                    filteredComponents.length === 0 &&
                    !componentsLoading &&
                    !testsLoading &&
                    searchQuery.trim() && (
                      <div className="px-3 py-2">
                        <div className="space-y-2">
                          <Text
                            size="sm"
                            className="font-medium text-gray-700 dark:text-gray-300"
                          >
                            Use a URL:
                          </Text>
                          <div
                            role="option"
                            tabIndex={0}
                            className="cursor-pointer rounded-md bg-blue-50 px-3 py-2 hover:bg-blue-100 dark:bg-blue-950 dark:hover:bg-blue-900"
                            onClick={() => {
                              form.setFieldValue('websiteUnderTest', searchQuery);
                              setSelectedTestToCopy(null);
                              setSelectedComponentId(null);
                              combobox.closeDropdown();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                form.setFieldValue('websiteUnderTest', searchQuery);
                                setSelectedTestToCopy(null);
                                setSelectedComponentId(null);
                                combobox.closeDropdown();
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <IconLink
                                size={16}
                                className="shrink-0 text-blue-500"
                              />
                              <Text
                                size="sm"
                                className="break-all font-medium text-blue-700 dark:text-blue-300"
                              >
                                {searchQuery}
                              </Text>
                            </div>
                          </div>
                          <Text
                            size="xs"
                            className="text-gray-500 dark:text-gray-400"
                          >
                            Press Enter or click to use
                          </Text>
                        </div>
                      </div>
                    )}

                  {filteredWebsites.length === 0 &&
                    filteredRecentTests.length === 0 &&
                    filteredComponents.length === 0 &&
                    !componentsLoading &&
                    !testsLoading &&
                    !searchQuery.trim() && (
                      <div className="px-3 py-2">
                        <Text
                          size="sm"
                          className="text-gray-500"
                        >
                          No results found
                        </Text>
                      </div>
                    )}
                </ScrollArea.Autosize>
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
          <Text className="mt-2 text-xs text-gray-600 dark:text-gray-400">
            Type a URL (https://example.com) or select from dropdown
          </Text>
          {isLocalhostLike(form.values.websiteUnderTest) && (
            <Alert
              color="yellow"
              radius="md"
              className="mt-2"
            >
              To test local apps, install the <Code>Stably CLI</Code> and run <Code>stably dev</Code> to create a secure
              tunnel.{' '}
              <Anchor
                href={LOCALHOST_TUNNEL_DOC}
                target="_blank"
                rel="noreferrer"
              >
                Learn more
              </Anchor>
              .
            </Alert>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            rightSection={<IconCornerDownLeft size={13} />}
            type="submit"
            disabled={
              projectId === undefined ||
              (!selectedComponentId && !selectedTestToCopy && isLocalhostLike(form.values.websiteUnderTest))
            }
            loading={isCreating}
            radius="md"
            size="sm"
            color="blue"
          >
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}
